import {
    ChangeEvent,
    useState,
} from 'react';

import {
    PublicLeadPayload,
    submitPublicLead,
} from '../services/publicLeadService';

export type PublicLeadSubmitStatus =
    | 'idle'
    | 'success'
    | 'error';

type StringFormState = Record<string, string>;

interface UsePublicLeadFormOptions<
    TFormState extends StringFormState
> {
    initialValues: TFormState;
    requiredFields: Array<keyof TFormState>;
    buildPayload: (
        values: TFormState
    ) => PublicLeadPayload;
}

export const usePublicLeadForm = <
    TFormState extends StringFormState
>({
    initialValues,
    requiredFields,
    buildPayload,
}: UsePublicLeadFormOptions<TFormState>) => {
    const [
        formState,
        setFormState,
    ] = useState<TFormState>(initialValues);

    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState(false);

    const [
        submitStatus,
        setSubmitStatus,
    ] = useState<PublicLeadSubmitStatus>('idle');

    const handleFormChange = (
        event: ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement
        >
    ) => {
        const {
            name,
            value,
        } = event.target;

        setFormState((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const hasMissingRequiredField =
            requiredFields.some((field) => {
                const value = formState[field];

                return !value.trim();
            });

        if (
            hasMissingRequiredField
            || isSubmitting
        ) {
            return;
        }

        setIsSubmitting(true);

        try {
            const payload =
                buildPayload(formState);

            await submitPublicLead(payload);

            setSubmitStatus('success');
            setFormState(initialValues);
        } catch (error) {
            console.error(
                'Error enviando lead:',
                error
            );

            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        formState,
        isSubmitting,
        submitStatus,
        handleFormChange,
        handleSubmit,
    };
};